import { useEffect, useRef } from 'react';
import { useAiChatContextSafe } from 'juneau';
import { nowPlainDate } from '@insio/dates';
import { config } from '@/config';
import { isEmptyAssistantMessage } from './empty-assistant-message';

/** Per-day marker so a reload does not re-open the panel. The server enforces this too. */
const STORAGE_KEY = 'tappeer.ai-chat.greeted';

/** Today as YYYY-MM-DD in the local zone — the greeting is a per-day event. */
const today = (): string => nowPlainDate().toString();

/**
 * True when this browser has not yet seen today's greeting.
 * The debug flag skips the per-day marker so every reload greets again.
 */
export function shouldGreet(userId: string, storage: Pick<Storage, 'getItem'>): boolean {
  if (config.aiChatGreetingAlways) return true;
  try {
    return storage.getItem(`${STORAGE_KEY}.${userId}`) !== today();
  } catch {
    // Private mode / disabled storage — the server gate still prevents a repeat.
    return true;
  }
}

/** Records that this browser has shown today's greeting. */
export function markGreeted(userId: string, storage: Pick<Storage, 'setItem'>): void {
  try {
    storage.setItem(`${STORAGE_KEY}.${userId}`, today());
  } catch {
    // Nothing to do — the server marker is authoritative.
  }
}

interface Options {
  /** Null until the session is known — the hook waits for it. */
  readonly userId: string | null;
  /** False keeps the assistant silent; the server refuses the turn as well. */
  readonly enabled: boolean;
  /** Called just before the turn starts — the panel is open by default, so this is optional. */
  readonly onGreet?: () => void;
}

/**
 * Makes the assistant speak first, once per day. The hint text is deliberately
 * ignored by the adapter: it flips the request into GREETING mode and the server
 * builds the briefing from the caller's own data.
 */
export function useGreetingTrigger({ userId, enabled, onGreet }: Options): void {
  const chat = useAiChatContextSafe();
  const sendGreeting = chat?.sendGreeting;
  const isLoading = chat?.isLoading ?? false;
  const error = chat?.error ?? null;
  const messages = chat?.messages;
  const reset = chat?.reset;

  // One attempt per mount, even though React may run effects twice in StrictMode.
  const firedRef = useRef(false);
  // True between firing the greeting and learning how it ended.
  const pendingRef = useRef(false);
  // The turn only counts as started once loading has actually been observed.
  const startedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current || !enabled || !userId || !sendGreeting) return;
    if (!shouldGreet(userId, window.localStorage)) return;

    firedRef.current = true;
    pendingRef.current = true;
    onGreet?.();

    // sendGreeting resolves even when the turn failed — it reports failure through
    // `error`, never by rejecting. The outcome is therefore read from rendered state below.
    void sendGreeting('greeting');
  }, [userId, enabled, sendGreeting, onGreet]);

  // The day is marked only once a greeting has actually been delivered, so a failed
  // turn does not cost the user their daily greeting.
  useEffect(() => {
    if (!pendingRef.current || !userId) return;

    if (isLoading) {
      startedRef.current = true;
      return;
    }
    // Still the pre-send render — the stream has not begun yet.
    if (!startedRef.current) return;

    pendingRef.current = false;
    if (error !== null) return;

    // A declined greeting is still marked: the server has claimed the day already.
    markGreeted(userId, window.localStorage);

    // The server declined with `done` alone — drop juneau's placeholder, not an empty bubble.
    const last = messages?.at(-1);
    if (messages && last && isEmptyAssistantMessage(last)) reset?.(messages.slice(0, -1));
  }, [isLoading, error, userId, messages, reset]);
}
